import {
  AvBandwidthModeType,
  AvMonitoringEventTypeType,
  AvSubsystemAvServiceType,
  DeviceErrors,
} from "@minervaproject/avsubsystem";

import { ForumAPI } from "contexts/forum_api_context";
import { AvServiceType } from "shared/types";
import { MonitoringEventTypeType } from "shared/types/monitoring_event";
import { BandwidthModeType } from "shared/types/user";

export const getLicodeToken = async (
  forumAPI: ForumAPI,
  roomId: string,
  userId: string,
  licodeServer: string,
) => {
  const licodeNuveUrl = licodeServer.replace(
    /^(.*)\.av\.minervaproject\.com/,
    "$1-nuve.av.minervaproject.com",
  );
  const token = await forumAPI.post(
    "api/v1/licode/create-video-token",
    JSON.stringify({
      username: `user${userId}`,
      role: "presenter",
      "room-name": roomId,
      "licode-nuve-server": licodeNuveUrl,
    }),
  );
  if (!token || !token.ok) {
    const error = `Error from server API creating licode token ${token?.status}: ${token?.statusText}`;
    throw new Error(error);
  }
  const tokenText = await token.text();
  return {
    apiKey: "licodeKey",
    token: tokenText,
  };
};

export const getOpentokToken = async (forumAPI: ForumAPI, sessionId: string) => {
  const response = await forumAPI.post(
    "api/v1/opentok/create-video-token",
    JSON.stringify({
      "session-id": sessionId,
    }),
  );
  if (!response || !response.ok) {
    const error = `Error from server API creating opentok token ${response?.status}: ${response?.statusText}`;
    throw new Error(error);
  }
  const jsonResponse = await response.json();

  if (!jsonResponse || !jsonResponse.token || !jsonResponse["api-key"]) {
    throw new Error(`Error creating OpenTok token ${response?.status}: ${response?.statusText}`);
  }
  return {
    apiKey: jsonResponse["api-key"],
    token: jsonResponse.token,
  };
};

export const getDailyToken = async (forumAPI: ForumAPI, userId: string, roomId: string) => {
  window.flightLogger.log(`In getDailyToken, roomId: ${roomId}, userId: ${userId}`);
  const result = await forumAPI.post(
    "api/v1/av/drc",
    JSON.stringify({ "room-name": roomId, "user-id": userId }),
  );
  if (!result.ok) {
    throw new Error(
      `Error getting Daily token, status: ${result.status}, statusText: ${result.statusText}`,
    );
  }
  const data = await result.json();
  return {
    apiKey: "dailyKey",
    token: data.token,
  };
};

export const getAvBandwidthModeFromUserBandwidthMode = (bandwidthMode: BandwidthModeType) => {
  switch (bandwidthMode) {
    case BandwidthModeType.AudioOnly:
      return AvBandwidthModeType.AudioOnly;
    case BandwidthModeType.AutomaticBandwidth:
      return AvBandwidthModeType.AutomaticBandwidth;
    case BandwidthModeType.LimitedBandwidth:
      return AvBandwidthModeType.LimitedBandwidth;
    case BandwidthModeType.AudioOnlyForced:
      return AvBandwidthModeType.AudioOnly;
    default:
      return AvBandwidthModeType.AutomaticBandwidth;
  }
};

export const getMonitoringEventTypefromAvType = (avType: AvMonitoringEventTypeType) => {
  switch (avType) {
    case AvMonitoringEventTypeType.DEVICES_SELECTED:
      return MonitoringEventTypeType.DEVICES_SELECTED;
    case AvMonitoringEventTypeType.DEVICES_ERROR:
      return MonitoringEventTypeType.DEVICES_ERROR;
    case AvMonitoringEventTypeType.FAILED_CONNECTION_ALERT:
      return MonitoringEventTypeType.FAILED_CONNECTION_ALERT;
    case AvMonitoringEventTypeType.AV_CONNECTION_QUALITY_CHANGED:
      return MonitoringEventTypeType.AV_CONNECTION_QUALITY_CHANGED;
    case AvMonitoringEventTypeType.STREAM_STARTED:
      return MonitoringEventTypeType.STREAM_STARTED;
    case AvMonitoringEventTypeType.STREAM_FAILED:
      return MonitoringEventTypeType.STREAM_FAILED;
    case AvMonitoringEventTypeType.AV_REFRESH:
      return MonitoringEventTypeType.AV_REFRESH;
    case AvMonitoringEventTypeType.RELOAD_STREAM:
      return MonitoringEventTypeType.RELOAD_STREAM;
    default:
      return MonitoringEventTypeType.AV_REFRESH;
  }
};

export const getAvServiceFromAvServiceType = (avServiceType: AvServiceType) => {
  switch (avServiceType) {
    case AvServiceType.Fake:
      return AvSubsystemAvServiceType.Fake;
    case AvServiceType.Licode:
      return AvSubsystemAvServiceType.Licode;
    case AvServiceType.Opentok:
      return AvSubsystemAvServiceType.Opentok;
    case AvServiceType.Daily:
      return AvSubsystemAvServiceType.Daily;
    default:
      return AvSubsystemAvServiceType.Fake;
  }
};

export function deviceErrorForDisplay(error: DeviceErrors | null) {
  const errorToDisplay: { [key: string]: string } = {
    // [DeviceErrors.AUDIO_MAC_NOT_ALLOWED]: "audio_mac_not_allowed",
    [DeviceErrors.AUDIO_WINDOWS_CAMERA_IN_USE]: "Mic In Use by Another App (Windows)",
    [DeviceErrors.AUDIO_NOT_ALLOWED]: "Mic Blocked",
    [DeviceErrors.AUDIO_NOT_FOUND]: "No Mic Detected",
    // [DeviceErrors.AUDIO_FAIL]: "audio_fail",
    // [DeviceErrors.SCREENSHARE_FAIL]: "screenshare_fail",
    // [DeviceErrors.VIDEO_MAC_NOT_ALLOWED]: "video_mac_not_allowed",
    [DeviceErrors.VIDEO_WINDOWS_CAMERA_IN_USE]: "Video In Use by Another App (Windows)",
    [DeviceErrors.VIDEO_NOT_ALLOWED]: "Camera Blocked",
    [DeviceErrors.VIDEO_NOT_FOUND]: "No Camera Detected",
    // [DeviceErrors.VIDEO_FAIL]: "video_fail",
    // [DeviceErrors.MEDIA_TIMEOUT]: "media_timeout",
  };

  if (error === null) {
    return "Unknown error";
  }

  return errorToDisplay[error] || error;
}

export const avLoggerSink = {
  log: (...args: unknown[]) => {
    window.flightLogger.log(...args);
  },
  warn: (...args: unknown[]) => {
    window.flightLogger.warn(...args);
  },
  error: (...args: unknown[]) => {
    window.flightLogger.error(...args);
  },
};
